import { defineStore } from 'pinia';
import { AllPermission, SearchParams, UserInformation } from '@/types/user';
import { UserPermissions } from '~/plugins/services/auth/types';
import { ValueLabelPair } from '~/types';

const DEFAULT_SEARCH_PARAMS = {
  limit: 15,
  orderBy: 'firstName',
  desc: false,
  search: '',
  total: 0,
  offset: 0,
  active: 1,
  virtualContext: 0,
};

export const useUsersStore = defineStore('users', () => {
  const users = ref<Array<UserInformation>>([]);
  const canGoNext = ref<boolean>(false);
  const activePage = ref<number>(1);
  const searchParams = ref<SearchParams>({ ...DEFAULT_SEARCH_PARAMS });
  const isLoading = ref<boolean>(false);
  const allPermissions = ref<AllPermission>({
    permissions: [],
    relations: {},
  });
  const allRoles = ref<ValueLabelPair[]>([]);

  const clearUsers = (): void => {
    users.value = [];
  };

  const resetSearchParams = (): void => {
    searchParams.value = { ...DEFAULT_SEARCH_PARAMS };
  };

  // TODO - decide what to do with this.
  // This endpoint is deprecated, as currently administration is done through the user administrator portal on admin.brand.se
  // This method is called on UserAdministratorListUsers component, which is inaccessible on every brand, as clicking on user administration redirects to the administrator portal
  // This endpoint also uses the v2 version of getUsers endpoint, when v3 is available.
  // https://jira.dahl.se/browse/ECOM-7902
  const fetchUsers = async (): Promise<void> => {
    const { $authService } = useNuxtApp();
    const params = { ...searchParams.value };
    params.search = params.search || undefined;
    params.desc = params.desc || undefined;
    params.total = params.total || undefined;
    params.active = 1;
    params.offset = (activePage.value - 1) * params.limit!;

    isLoading.value = true;
    const { data } = await $authService.getUsers({ params });
    isLoading.value = false;

    if (!data) {
      return;
    }

    users.value = data.users;
    canGoNext.value = activePage.value * searchParams.value.limit! < data.totalNumberOfUsers;
    searchParams.value.total = data.totalNumberOfUsers;
  };

  const patchSearchParams = (payload: SearchParams): void => {
    searchParams.value = { ...searchParams.value, ...payload };
    activePage.value = 1;
    fetchUsers();
  };

  const fetchUserPermissions = async ({
    userId,
    params,
  }: {
    userId: string;
    params: SearchParams;
  }): Promise<UserPermissions> => {
    const { $authService } = useNuxtApp();
    const { data } = await $authService.getUserPermissions(userId, { params });
    if (!data) {
      return {};
    }
    return data || {};
  };

  const fetchAllRoles = async (): Promise<void> => {
    const { $authService } = useNuxtApp();
    const { data } = await $authService.getRoles();
    if (!data) {
      return;
    }

    allRoles.value = data;
  };

  const fetchAllPermissions = async (): Promise<void> => {
    const { $authService } = useNuxtApp();
    const { data } = await $authService.getPermissions();
    if (!data) {
      return;
    }
    allPermissions.value = data;
  };

  const getUserById = (userId: string) =>
    computed<UserInformation | undefined>(() => {
      return users.value.find((u: { userId: string }) => u.userId === userId);
    });

  return {
    users,
    searchParams,
    isLoading,
    allPermissions,
    allRoles,
    activePage,
    canGoNext,
    fetchUsers,
    clearUsers,
    resetSearchParams,
    patchSearchParams,
    fetchUserPermissions,
    fetchAllRoles,
    fetchAllPermissions,
    getUserById,
  };
});
